import React, { useEffect, useState, useCallback } from "react";
import {
  Layers, Plus, Trash2, Edit3, Search, X, Sprout, ChevronRight, Boxes, ChefHat,
  Soup, UtensilsCrossed, Target, AlertTriangle, Globe2, Store, Undo2, ListChecks,
} from "lucide-react";
import api from "../lib/api";
import { Card, Badge, Button, PageHeader, PageLoader, Modal } from "../lib/ui";
import { ProductAvatar } from "../components/visual";
import { fmtNum } from "../lib/constants";
import { useToast } from "../lib/toast";

const CLASS_TONE = { RTC: "warn", SEMI_FINISHED: "success", FINISHED: "info" };
const LEGEND = [
  { label: "Bahan Baku", desc: "Bahan mentah belum diolah", icon: Boxes, tone: "bg-amber-50 text-amber-600" },
  { label: "Bahan Siap Masak", desc: "Hasil persiapan, siap dimasak", icon: UtensilsCrossed, tone: "bg-orange-50 text-orange-600" },
  { label: "Barang Setengah Jadi", desc: "Saus, bumbu, produk setengah jadi, dll", icon: Soup, tone: "bg-emerald-50 text-emerald-600" },
  { label: "Produk Jadi", desc: "Menu siap jual ke pelanggan", icon: ChefHat, tone: "bg-violet-50 text-violet-600" },
];
const GLOBAL_KEY = "__global__";

export default function MasterPersiapan() {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [selId, setSelId] = useState(null);
  const [outputModal, setOutputModal] = useState(null); // {mode:'add'|'edit', output?}
  const [ruleModal, setRuleModal] = useState(null); // {output, scope, branch_id?}
  const [addRawOpen, setAddRawOpen] = useState(false);
  const [branchId, setBranchId] = useState(GLOBAL_KEY); // '__global__' or branch id

  const load = useCallback(async (bid = branchId) => {
    const params = bid && bid !== GLOBAL_KEY ? { branch_id: bid } : {};
    const { data } = await api.get("/prepare/master", { params });
    setData(data);
    setSelId((cur) => {
      if (cur && data.raw_materials.some((r) => r.source_product_id === cur)) return cur;
      return data.raw_materials[0]?.source_product_id || null;
    });
  }, [branchId]);
  useEffect(() => { load(); }, [branchId]);

  if (!data) return <PageLoader />;
  const sel = data.raw_materials.find((r) => r.source_product_id === selId) || null;
  const isBranchView = branchId !== GLOBAL_KEY;
  const activeBranch = (data.branches || []).find((b) => b.id === branchId);

  const removeRaw = async (raw) => {
    if (!window.confirm(`Hapus "${raw.source_name}" dari Master Persiapan? Pemetaan output akan dilepas.`)) return;
    try { await api.delete(`/prepare/master/raw/${raw.source_product_id}`); toast.success("Bahan baku dihapus dari master"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const removeOutput = async (o) => {
    if (!window.confirm(`Nonaktifkan output "${o.output_name}"?`)) return;
    try { await api.delete(`/prepare/master/output/${o.output_product_id}`); toast.success("Output dinonaktifkan"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const resetOverride = async (o) => {
    if (!window.confirm(`Reset "${o.output_name}" ke aturan Global untuk cabang ${activeBranch?.name}?`)) return;
    try {
      await api.delete(`/prepare/master/output/${o.output_product_id}/rule/branch/${branchId}`);
      toast.success("Override cabang direset ke Global"); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  return (
    <div className="animate-fade-up pb-24 lg:pb-2" data-testid="master-persiapan">
      <PageHeader icon={Layers} title="Master Persiapan"
        subtitle="Owner menentukan target persiapan per output — Meja Prepare akan otomatis menghitung antrian harian.">
        <div className="flex items-center gap-2 rounded-xl border border-line bg-white px-3 py-2 shadow-sm" data-testid="mp-branch-scope">
          <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${isBranchView ? "bg-orange-50 text-orange-600" : "bg-primary-light text-primary"}`}>
            {isBranchView ? <Store size={16} /> : <Globe2 size={16} />}
          </span>
          <div className="leading-tight">
            <p className="text-[10px] font-semibold uppercase text-ink-mute">Lingkup Aturan</p>
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)} data-testid="mp-branch-select"
              className="-ml-0.5 cursor-pointer bg-transparent text-sm font-bold text-ink focus:outline-none">
              <option value={GLOBAL_KEY}>Global (Default)</option>
              {(data.branches || []).map((b) => <option key={b.id} value={b.id}>Cabang · {b.name}</option>)}
            </select>
          </div>
        </div>
      </PageHeader>

      {isBranchView && (
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-xs text-orange-800" data-testid="mp-branch-banner">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>
            <p className="font-bold">Sedang melihat aturan cabang <b>{activeBranch?.name}</b>.</p>
            <p>Nilai yang ditandai <b>Override</b> menggantikan Global khusus untuk cabang ini. Field yang tidak di-override tetap mengikuti Global. Override cabang hanya berlaku untuk Bahan Siap Masak.</p>
          </div>
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-12">
        {/* Left: raw material list */}
        <div className="lg:col-span-4">
          <Card className="p-0" data-testid="master-raw-list">
            <div className="border-b border-line px-4 py-3"><h3 className="font-heading text-sm font-extrabold text-ink">Daftar Bahan Baku</h3></div>
            <div className="max-h-[60vh] divide-y divide-line overflow-y-auto">
              {data.raw_materials.length === 0 && <p className="px-4 py-8 text-center text-sm text-ink-mute">Belum ada bahan baku. Tambahkan di bawah.</p>}
              {data.raw_materials.map((r) => {
                const on = r.source_product_id === selId;
                return (
                  <button key={r.source_product_id} onClick={() => setSelId(r.source_product_id)} data-testid={`master-raw-${r.source_product_id}`}
                    className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${on ? "bg-primary-light" : "hover:bg-stone-50"}`}>
                    <ProductAvatar image={r.image} colorKey={r.category_name} name={r.source_name} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className={`truncate font-bold ${on ? "text-primary" : "text-ink"}`}>{r.source_name}</p>
                      <p className="text-[11px] text-ink-mute">{r.output_count} output</p>
                    </div>
                    <ChevronRight size={16} className={on ? "text-primary" : "text-ink-mute"} />
                  </button>
                );
              })}
            </div>
            <div className="border-t border-line p-3">
              {!isBranchView && (
                <Button variant="outline" className="w-full" onClick={() => setAddRawOpen(true)} data-testid="master-add-raw-btn"><Plus size={15} /> Tambah Bahan Baku</Button>
              )}
              {isBranchView && (
                <p className="text-center text-[11px] text-ink-mute">Kembali ke <b>Global</b> untuk menambah bahan baku.</p>
              )}
            </div>
          </Card>
        </div>

        {/* Right: outputs of selected raw */}
        <div className="lg:col-span-8">
          {!sel ? (
            <Card className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <Sprout size={32} className="text-ink-mute" />
              <p className="text-sm text-ink-mute">Pilih atau tambahkan bahan baku untuk mengelola output persiapan.</p>
            </Card>
          ) : (
            <Card className="p-0" data-testid="master-output-panel">
              <div className="flex items-center gap-3 border-b border-line px-5 py-4">
                <ProductAvatar image={sel.image} colorKey={sel.category_name} name={sel.source_name} size="md" />
                <div className="min-w-0 flex-1">
                  <h3 className="font-heading text-lg font-extrabold text-ink">{sel.source_name}</h3>
                  <p className="text-xs text-ink-mute">Kelola output & aturan persiapan untuk {sel.source_name.toLowerCase()}</p>
                </div>
                {!isBranchView && (
                  <>
                    <Button onClick={() => setOutputModal({ mode: "add" })} data-testid="master-add-output-btn"><Plus size={16} /> Tambah Output</Button>
                    <button onClick={() => removeRaw(sel)} title="Hapus bahan baku dari master" className="flex h-9 w-9 items-center justify-center rounded-lg border border-line text-ink-mute hover:border-danger hover:text-danger-text" data-testid="master-del-raw"><Trash2 size={15} /></button>
                  </>
                )}
              </div>
              {sel.outputs.length === 0 ? (
                <div className="py-12 text-center"><p className="text-sm text-ink-mute">Belum ada output. Kembali ke Global untuk menambah output.</p></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="master-output-table">
                    <thead className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-mute">
                      <tr>
                        <th className="px-5 py-2.5">Output</th>
                        <th className="px-3 py-2.5">Kategori</th>
                        <th className="px-3 py-2.5 text-right">Target Standby</th>
                        <th className="px-3 py-2.5 text-right">Minimum</th>
                        <th className="px-3 py-2.5 text-center">Aktif</th>
                        <th className="px-3 py-2.5 text-right">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line">
                      {sel.outputs.map((o) => (
                        <OutputRow key={o.output_product_id} o={o} isBranchView={isBranchView}
                          canOverride={isBranchView && o.branch_override_supported}
                          onEditGlobal={() => setOutputModal({ mode: "edit", output: o })}
                          onEditRule={() => setRuleModal({ output: o, scope: isBranchView ? "branch" : "global", branch_id: isBranchView ? branchId : null })}
                          onRemove={() => removeOutput(o)}
                          onResetOverride={() => resetOverride(o)} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}

          {/* Legend */}
          <Card className="mt-5 p-4">
            <p className="mb-3 text-center text-xs font-bold uppercase tracking-wide text-ink-mute">Kategori Inventory (Tampil di Seluruh Aplikasi)</p>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {LEGEND.map((l) => (
                <div key={l.label} className="flex items-center gap-2.5">
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${l.tone}`}><l.icon size={18} /></span>
                  <div className="min-w-0"><p className="text-xs font-bold text-ink">{l.label}</p><p className="text-[10px] leading-tight text-ink-mute">{l.desc}</p></div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {outputModal && sel && !isBranchView && (
        <OutputModal mode={outputModal.mode} output={outputModal.output} raw={sel} categories={data.categories}
          onClose={() => setOutputModal(null)} onSaved={() => { setOutputModal(null); load(); }} />
      )}
      {ruleModal && (
        <RuleModal output={ruleModal.output} scope={ruleModal.scope} branchId={ruleModal.branch_id}
          branchName={activeBranch?.name} onClose={() => setRuleModal(null)}
          onSaved={() => { setRuleModal(null); load(); }} />
      )}
      {addRawOpen && !isBranchView && (
        <AddRawModal addable={data.addable_raws} onClose={() => setAddRawOpen(false)}
          onAdded={(rid) => { setAddRawOpen(false); setSelId(rid); load(); }} />
      )}
    </div>
  );
}

function OutputRow({ o, isBranchView, canOverride, onEditGlobal, onEditRule, onRemove, onResetOverride }) {
  const overridden = (f) => isBranchView && (o.override_fields || []).includes(f);
  const renderCell = (v, unit, field) => (
    <span className={`inline-flex items-center gap-1 font-semibold ${overridden(field) ? "text-orange-700" : "text-ink"}`}>
      {v == null ? <span className="text-ink-mute">—</span> : <>{fmtNum(v)} <span className="text-[10px] text-ink-mute">{unit}</span></>}
      {overridden(field) && <Badge tone="warn" className="ml-1 !text-[9px]">Override</Badge>}
    </span>
  );
  const activeOverridden = overridden("active");
  return (
    <tr className={o.active ? "" : "opacity-50"} data-testid={`master-output-row-${o.output_product_id}`}>
      <td className="px-5 py-3">
        <div className="flex flex-col">
          <span className="font-semibold text-ink">{o.output_name}{!o.active && <span className="ml-2 text-[10px] font-bold uppercase text-ink-mute">(nonaktif)</span>}</span>
          {isBranchView && !o.branch_override_supported && <span className="mt-0.5 text-[10px] text-ink-mute">Global-only (Barang Setengah Jadi)</span>}
        </div>
      </td>
      <td className="px-3 py-3"><Badge tone={CLASS_TONE[o.production_class] || "mute"}>{o.category_label}</Badge></td>
      <td className="px-3 py-3 text-right" data-testid={`master-target-${o.output_product_id}`}>
        {renderCell(o.target_qty, o.unit, "target_qty")}
      </td>
      <td className="px-3 py-3 text-right" data-testid={`master-min-${o.output_product_id}`}>
        {renderCell(o.min_qty, o.unit, "min_qty")}
      </td>
      <td className="px-3 py-3 text-center">
        <span className={`inline-flex items-center gap-1 font-semibold ${activeOverridden ? "text-orange-700" : (o.prep_rule_active ? "text-emerald-600" : "text-ink-mute")}`} data-testid={`master-active-${o.output_product_id}`}>
          {o.prep_rule_active ? "Aktif" : "Nonaktif"}
          {activeOverridden && <Badge tone="warn" className="!text-[9px]">Override</Badge>}
        </span>
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center justify-end gap-1.5">
          {isBranchView ? (
            <>
              {canOverride && (
                <>
                  <button onClick={onEditRule} title={`Atur override cabang`} className="flex h-8 items-center gap-1 rounded-lg border border-line px-2 text-xs font-bold text-ink-soft hover:border-orange-400 hover:text-orange-600" data-testid={`master-override-${o.output_product_id}`}>
                    <Target size={13} /> Override
                  </button>
                  {o.override_applied && (
                    <button onClick={onResetOverride} title="Kembalikan ke Global" className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-ink-mute hover:border-primary hover:text-primary" data-testid={`master-reset-${o.output_product_id}`}>
                      <Undo2 size={14} />
                    </button>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <button onClick={onEditRule} title="Atur aturan persiapan Global" className="flex h-8 items-center gap-1 rounded-lg border border-line px-2 text-xs font-bold text-ink-soft hover:border-primary hover:text-primary" data-testid={`master-rule-${o.output_product_id}`}>
                <ListChecks size={13} /> Aturan
              </button>
              <button onClick={onEditGlobal} className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-ink-soft hover:border-primary hover:text-primary" data-testid={`master-edit-${o.output_product_id}`}><Edit3 size={14} /></button>
              <button onClick={onRemove} className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-ink-mute hover:border-danger hover:text-danger-text" data-testid={`master-del-${o.output_product_id}`}><Trash2 size={14} /></button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

function RuleModal({ output, scope, branchId, branchName, onClose, onSaved }) {
  const toast = useToast();
  const isBranch = scope === "branch";
  // For branch mode, prefill with the RESOLVED effective value (from API); user may override individual fields.
  const [target, setTarget] = useState(output.target_qty != null ? String(output.target_qty) : "");
  const [minQty, setMinQty] = useState(output.min_qty != null ? String(output.min_qty) : "");
  const [active, setActive] = useState(!!output.prep_rule_active);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const body = {};
    body[isBranch ? "active" : "prep_rule_active"] = active;
    body.target_qty = target === "" ? null : Number(target);
    body.min_qty = minQty === "" ? null : Number(minQty);
    if (body.target_qty != null && Number.isNaN(body.target_qty)) return toast.error("Target harus berupa angka");
    if (body.min_qty != null && Number.isNaN(body.min_qty)) return toast.error("Minimum harus berupa angka");
    setBusy(true);
    try {
      if (isBranch) {
        await api.put(`/prepare/master/output/${output.output_product_id}/rule/branch/${branchId}`, body);
        toast.success(`Override cabang ${branchName} disimpan`);
      } else {
        await api.put(`/prepare/master/output/${output.output_product_id}/rule`, body);
        toast.success("Aturan Global disimpan");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); } finally { setBusy(false); }
  };

  const title = isBranch
    ? `Override Cabang · ${branchName} · ${output.output_name}`
    : `Aturan Global · ${output.output_name}`;

  return (
    <Modal open onClose={onClose} title={title} size="md"
      footer={<div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose} data-testid="rule-cancel-btn">Batal</Button><Button onClick={save} loading={busy} data-testid="rule-save-btn">Simpan</Button></div>}>
      <div className="space-y-4" data-testid="rule-modal">
        {isBranch && (
          <div className="flex items-start gap-2 rounded-xl bg-orange-50 px-3 py-2.5 text-xs text-orange-800">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div>Nilai yang dikosongkan akan otomatis mengikuti aturan <b>Global</b>. Simpan hanya field yang ingin di-override untuk cabang <b>{branchName}</b>.</div>
          </div>
        )}
        {!isBranch && (
          <div className="flex items-start gap-2 rounded-xl bg-primary-light px-3 py-2.5 text-xs text-primary">
            <Globe2 size={14} className="mt-0.5 shrink-0" />
            <div>Aturan Global ini berlaku untuk seluruh cabang, kecuali cabang yang memiliki <b>Override</b> khusus.</div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-semibold text-ink-soft">Target Standby ({output.unit})</span>
            <input type="number" min="0" step="0.1" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="rule-target-input"
              placeholder="mis. 2" className="mt-1 w-full rounded-xl border border-line py-2.5 px-3 text-sm font-bold focus:border-primary focus:outline-none" />
            <p className="mt-1 text-[10px] text-ink-mute">Jumlah stok siap sedia sebelum operasional.</p>
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-ink-soft">Minimum ({output.unit})</span>
            <input type="number" min="0" step="0.1" value={minQty} onChange={(e) => setMinQty(e.target.value)} data-testid="rule-min-input"
              placeholder="mis. 0.5" className="mt-1 w-full rounded-xl border border-line py-2.5 px-3 text-sm font-bold focus:border-primary focus:outline-none" />
            <p className="mt-1 text-[10px] text-ink-mute">Ambang peringatan (stok di bawah ini = kritis).</p>
          </label>
        </div>
        <label className="flex items-center gap-2.5 rounded-xl border border-line p-3">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="rule-active-toggle" className="h-4 w-4 accent-primary" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-ink">Aturan Aktif</p>
            <p className="text-[10px] text-ink-mute">Jika nonaktif atau Target kosong/0, output ini tidak muncul di Antrian Persiapan.</p>
          </div>
        </label>
      </div>
    </Modal>
  );
}

function OutputModal({ mode, output, raw, categories, onClose, onSaved }) {
  const toast = useToast();
  const isEdit = mode === "edit";
  const [name, setName] = useState(output?.output_name || "");
  const [pc, setPc] = useState(output?.production_class || categories[0]?.key || "RTC");
  const [yld, setYld] = useState(output?.expected_yield_pct != null ? String(output.expected_yield_pct) : "");
  const [unit, setUnit] = useState(output?.unit || raw.unit || "kg");
  const [active, setActive] = useState(output?.active !== false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim()) return toast.error("Nama output wajib diisi");
    setBusy(true);
    try {
      const body = { output_name: name.trim(), production_class: pc, unit: unit.trim(),
        expected_yield_pct: yld === "" ? null : Number(yld), active };
      if (isEdit) await api.put(`/prepare/master/output/${output.output_product_id}`, body);
      else await api.post("/prepare/master/output", { source_product_id: raw.source_product_id, ...body });
      toast.success(isEdit ? "Output diperbarui" : "Output dibuat & tersedia di Resep/BOM");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={isEdit ? `Edit Output · ${raw.source_name}` : `Tambah Output · ${raw.source_name}`} size="md"
      footer={<div className="flex justify-end"><Button onClick={save} loading={busy} data-testid="output-save-btn">Simpan</Button></div>}>
      <div className="space-y-4" data-testid="output-modal">
        <label className="block">
          <span className="text-xs font-semibold text-ink-soft">Nama Output</span>
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus data-testid="output-name-input" placeholder={`mis. ${raw.source_name} Iris`}
            className="mt-1 w-full rounded-xl border border-line py-2.5 px-3 text-sm focus:border-primary focus:outline-none" />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-ink-soft">Kategori Inventory</span>
          <select value={pc} onChange={(e) => setPc(e.target.value)} data-testid="output-class-select"
            className="mt-1 w-full rounded-xl border border-line bg-white py-2.5 px-3 text-sm focus:border-primary focus:outline-none">
            {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <p className="mt-1 text-[11px] text-ink-mute">{categories.find((c) => c.key === pc)?.hint}</p>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-semibold text-ink-soft">Yield Standar (%)</span>
            <input type="number" min="0" max="100" step="0.1" value={yld} onChange={(e) => setYld(e.target.value)} data-testid="output-yield-input" placeholder="mis. 90"
              className="mt-1 w-full rounded-xl border border-line py-2.5 px-3 text-sm focus:border-primary focus:outline-none" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-ink-soft">Satuan</span>
            <input value={unit} onChange={(e) => setUnit(e.target.value)} data-testid="output-unit-input"
              className="mt-1 w-full rounded-xl border border-line py-2.5 px-3 text-sm focus:border-primary focus:outline-none" />
          </label>
        </div>
        <label className="flex items-center gap-2.5 rounded-xl border border-line p-3">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="output-active-toggle" className="h-4 w-4 accent-primary" />
          <span className="text-sm font-semibold text-ink">Aktif (tersedia di Meja Prepare & Resep)</span>
        </label>
      </div>
    </Modal>
  );
}

function AddRawModal({ addable, onClose, onAdded }) {
  const toast = useToast();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const list = addable.filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()));

  const add = async () => {
    if (!sel) return;
    setBusy(true);
    try { await api.post("/prepare/master/raw", { source_product_id: sel.id }); toast.success(`${sel.name} ditambahkan`); onAdded(sel.id); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Tambah Bahan Baku" size="md"
      footer={<div className="flex justify-end"><Button onClick={add} disabled={!sel} loading={busy} data-testid="addraw-confirm"><Plus size={16} /> Tambahkan</Button></div>}>
      <div className="space-y-3" data-testid="addraw-modal">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-mute" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="addraw-search" placeholder="Cari bahan baku…"
            className="w-full rounded-xl border border-line py-2.5 pl-9 pr-3 text-sm focus:border-primary focus:outline-none" />
        </div>
        <div className="max-h-72 space-y-1.5 overflow-y-auto pr-1">
          {list.length === 0 && <p className="py-6 text-center text-xs text-ink-mute">Semua bahan baku sudah ada di master.</p>}
          {list.map((r) => {
            const on = sel?.id === r.id;
            return (
              <button key={r.id} onClick={() => setSel(r)} data-testid={`addraw-pick-${r.id}`}
                className={`flex w-full items-center gap-2.5 rounded-xl border p-2.5 text-left transition ${on ? "border-primary bg-primary-light" : "border-line hover:bg-stone-50"}`}>
                <ProductAvatar image={r.image} colorKey={r.category_name} name={r.name} size="sm" />
                <div className="min-w-0 flex-1"><p className="truncate text-sm font-bold text-ink">{r.name}</p><p className="text-[10px] text-ink-mute">{r.category_name || "—"} · {r.unit}</p></div>
                {on && <X size={0} />}
              </button>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}
